import { useEffect, useState } from "react";
import {
  collection,
  onSnapshot,
  query,
  where,
  doc,
  updateDoc,
  Timestamp,
} from "firebase/firestore";
import { firestore as db } from "../../../firebase/firebase";
import toast from "react-hot-toast";

// Define interfaces for the data structure
export interface PatientAvailability {
  id: string;
  age: number;
  appointmentDate: string;
  appointmentId: string;
  appointmentTime: string;
  callStatus: "Active" | "Inactive" | "Completed";
  channelName: string;
  clinicId: string;
  doctorId: string;
  doctorname: string;
  education: string;
  gender: string;
  timestamp: Date;
  name: string;
  image: string;
  roomStatus: "IDLE" | "RINGING" | "ONGOING" | "ENDED";
}

interface UsePatientProps {
  clinicId: string;
  date: string;
}

const useAvailablePatient = ({ clinicId, date }: UsePatientProps) => {
  const [availabilities, setAvailabilities] = useState<PatientAvailability[]>(
    []
  );
  const [error, setError] = useState<string>("");
  const [previousData, setPreviousData] = useState<{
    [key: string]: PatientAvailability;
  }>({});

  const updateCallStatus = async (patientId: string) => {
    try {
      const docRef = doc(db, "patientAvailability", patientId);
      await updateDoc(docRef, {
        callStatus: "Active",
        roomStatus: "ONGOING",
        lastUpdated: new Date(),
      });
      return true;
    } catch (err) {
      console.error("Error updating call status:", err);
      setError("Failed to update call status");
      return false;
    }
  };

  useEffect(() => {
    let unsubscribe = () => { };

    const setupRealtimeListener = async () => {
      try {
        const patientAvailabilityRef = collection(db, "patientAvailability");

        // Add callStatus filter to query
        unsubscribe = onSnapshot(
          query(
            patientAvailabilityRef,
            where("clinicId", "==", clinicId),
            where("appointmentDate", "==", date),
            where("callStatus", "in", ["pending", "Active"]) // Add filter for active calls
          ),
          {
            next: (snapshot) => {
              const availabilityData = snapshot.docs.map((doc) => ({
                id: doc.id,
                ...doc.data(),
                timestamp:
                  doc.data().timestamp instanceof Timestamp
                    ? doc.data().timestamp.toDate()
                    : new Date(),
              })) as PatientAvailability[];

              // Check for status changes
              availabilityData.forEach((appointment) => {
                const previous = previousData[appointment.id];
                if (
                  previous &&
                  previous.callStatus !== "Active" &&
                  appointment.callStatus === "Active"
                ) {
                  // Show toast notification when status changes to Active
                  toast.success(
                    `Doctor is calling - Appointment with ${appointment.name} at ${appointment.appointmentTime}`,
                    {
                      duration: 5000,
                      position: "top-right",
                      style: {
                        background: "#fff",
                        color: "#333",
                        padding: "16px",
                        borderRadius: "8px",
                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                      },
                    }
                  );
                }
              });

              // Update previous data
              const newPreviousData = availabilityData.reduce(
                (acc, appointment) => {
                  acc[appointment.id] = appointment;
                  return acc;
                },
                {} as { [key: string]: PatientAvailability }
              );
              setPreviousData(newPreviousData);

              // Sort by timestamp (most recent first)
              availabilityData.sort(
                (a, b) => b.timestamp.getTime() - a.timestamp.getTime()
              );

              setAvailabilities(availabilityData);
              setError("");
            },
            error: (err) => {
              console.error("Error in real-time listener:", err);
              setError(
                "Failed to sync with the database. Please refresh the page."
              );
            },
          }
        );
      } catch (err) {
        console.error("Error setting up listener:", err);
        setError("Failed to initialize real-time updates");
      }
    };

    setupRealtimeListener();

    return () => {
      unsubscribe();
    };
  }, [date, clinicId]);

  return { availabilities, error, updateCallStatus };
};

export default useAvailablePatient;
